import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Detagli_Intervento from './Detagli_Intervento';
import StoricoRaddrizzatrice from './StoricoInterventiRD';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const TABLE_NAME = 'rd_cco';

// ─── Utility Export ───────────────────────────────────────────────────────────

const fmtDate = (val) => {
  if (!val) return '---';
  try { return new Date(val).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return val; }
};

const fmtDatetime = (val) => {
  if (!val) return '---';
  try { return new Date(val).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return val; }
};

const exportCSV = (data) => {
  if (!data || data.length === 0) return;

  const headers = ['Data Intervento', 'N° Macchina', 'Tipo Intervento', 'Linea', 'Colata', 'Posizione', 'Stato', 'Data Rientro', 'S/N Rientrato', 'S/N Smontato', 'S/N Montato', 'Stato RID', 'Causa Freno', 'Note'];

  const rows = data.map(r => [
    fmtDatetime(r.data_intervento),
    r.n_macchina ?? '',
    r.tipointervento ?? '',
    r.linea ?? '',
    r.colata ?? '',
    r.posizione ?? '',
    r.stato ?? '',
    fmtDatetime(r.data_rientro),
    r.snrientrato ?? '',
    r.snsmontato ?? '',
    r.snmontato ?? '',
    r.stato_rid ?? '',
    r.causa_freno ? 'SI' : 'NO',
    r.note ?? '',
  ]);

  const csv = [headers, ...rows].map(r =>
    r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
  ).join('\n');

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `raddrizzatrici_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const exportExcel = async (data) => {
  if (!data || data.length === 0) return;

  try {
    const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');

    const ws = XLSX.utils.json_to_sheet(data.map(r => ({
      'Data Intervento': fmtDatetime(r.data_intervento),
      'N° Macchina':     r.n_macchina ?? '',
      'Tipo Intervento': r.tipointervento ?? '',
      'Linea':           r.linea ?? '',
      'Colata':          r.colata ?? '',
      'Posizione':       r.posizione ?? '',
      'Stato':           r.stato ?? '',
      'Data Rientro':    fmtDatetime(r.data_rientro),
      'S/N Rientrato':   r.snrientrato ?? '',
      'S/N Smontato':    r.snsmontato ?? '',
      'S/N Montato':     r.snmontato ?? '',
      'Stato RID':       r.stato_rid ?? '',
      'Causa Freno':     r.causa_freno ? 'SI' : 'NO',
      'Note':            r.note ?? '',
    })));

    const wscols = [
      { wch: 20 }, // Data Intervento
      { wch: 15 }, // N Macchina
      { wch: 18 }, // Tipo Intervento
      { wch: 10 }, // Linea
      { wch: 12 }, // Colata
      { wch: 16 }, // Posizione
      { wch: 15 }, // Stato
      { wch: 20 }, // Data Rientro
      { wch: 16 }, // S/N Rientrato
      { wch: 16 }, // S/N Smontato
      { wch: 16 }, // S/N Montato
      { wch: 15 }, // Stato RID
      { wch: 12 }, // Causa Freno
      { wch: 40 }, // Note
    ];
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Raddrizzatrici');
    XLSX.writeFile(wb, `raddrizzatrici_${new Date().toISOString().slice(0,10)}.xlsx`);

  } catch (err) {
    console.error("Excel Error:", err);
    alert("Errore durante l'esportazione del file Excel.");
  }
};

const exportPDF = (data) => {
  if (!data || data.length === 0) return;

  try {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const now = new Date().toLocaleString('it-IT');

    // --- HEADER ---
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('ORIZONDESK', 14, 20);

    doc.setFontSize(14);
    doc.setTextColor(100);
    doc.text('REPORT RADDRIZZATRICI — CCO', 14, 28);

    doc.setFontSize(9);
    doc.text(`Interventi esportati: ${data.length}  |  Data stampa: ${now}`, 14, 34);

    // --- PREPARAZIONE DATI ---
    const tableData = data.map(r => [
      fmtDatetime(r.data_intervento),
      r.n_macchina ?? '---',
      r.tipointervento ?? '---',
      r.linea ?? '---',
      r.colata ?? '---',
      r.posizione ?? '---',
      r.stato ?? '---',
      fmtDatetime(r.data_rientro)
    ]);

    // --- GENERAZIONE TABELLA ---
    autoTable(doc, {
      startY: 40,
      head: [['Data Int.', 'N° Mac.', 'Tipo', 'Linea', 'Colata', 'Posizione', 'Stato', 'Rientro']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [15, 23, 42],
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center'
      },
      styles: {
        fontSize: 7.5,
        cellPadding: 2.5,
        overflow: 'linebreak'
      },
      columnStyles: {
        0: { cellWidth: 35 }, // Data Int.
        1: { cellWidth: 30 }, // N Mac.
        2: { cellWidth: 25, halign: 'center', fontStyle: 'bold' }, // Tipo
        3: { cellWidth: 20, halign: 'center' }, // Linea
        4: { cellWidth: 25, halign: 'center' }, // Colata
        5: { cellWidth: 35, halign: 'center' }, // Posizione
        6: { cellWidth: 30, halign: 'center', fontStyle: 'bold' }, // Stato
        7: { cellWidth: 30, halign: 'center' }  // Rientro
      },
      // Colorazione dinamica delle celle (Tipo e Stato)
      didParseCell: function(dataCell) {
        if (dataCell.section === 'body') {
          if (dataCell.column.index === 2) {
            const val = dataCell.cell.raw?.toUpperCase();
            if (val === 'MONTATO') dataCell.cell.styles.textColor = [22, 163, 74];
            else if (val === 'SMONTATO') dataCell.cell.styles.textColor = [220, 38, 38];
          }
          if (dataCell.column.index === 6) {
            const val = dataCell.cell.raw?.toUpperCase();
            if (val === 'PRONTO') dataCell.cell.styles.textColor = [22, 163, 74];
            else dataCell.cell.styles.textColor = [249, 115, 22];
          }
        }
      },
      didDrawPage: function(dataPage) {
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();

        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont('helvetica', 'normal');

        doc.text(`Stampato il: ${now}`, 14, pageHeight - 10);

        const middleText = `OrizonDesk - Pagina ${doc.internal.getNumberOfPages()}`;
        doc.text(middleText, pageWidth / 2, pageHeight - 10, { align: 'center' });

        const url = "https://desk.orizon-aix.com";
        doc.text(url, pageWidth - 14, pageHeight - 10, { align: 'right' });
      }
    });

    doc.save(`Raddrizzatrici_${new Date().toISOString().slice(0,10)}.pdf`);

  } catch (error) {
    console.error("PDF Export Error:", error);
    alert("Errore tecnico durante la generazione del PDF.");
  }
};

// ─── Colonne tabella (px fissi per mobile scroll) ─────────────────────────────
const COLONNE = [
  { label: 'Data Intervento', key: 'data_intervento', width: 150, align: 'left'   },
  { label: 'N° Macchina',     key: 'n_macchina',       width: 130, align: 'left'   },
  { label: 'Tipo',            key: 'tipointervento',   width: 120, align: 'center' },
  { label: 'Linea',           key: 'linea',            width: 90,  align: 'center' },
  { label: 'Colata',          key: 'colata',           width: 100, align: 'center' },
  { label: 'Posizione',       key: 'posizione',        width: 120, align: 'center' },
  { label: 'Causa Freno',     key: 'causa_freno',      width: 120, align: 'center' },
  { label: 'Stato',           key: 'stato',            width: 130, align: 'center' },
  { label: 'Data Rientro',    key: 'data_rientro',     width: 150, align: 'center' },
  { label: '',                key: '__azioni',         width: 150, align: 'center', noSort: true },
];

const DENSITA = { compatta: 'py-2', normale: 'py-4', spaziosa: 'py-6' };

// Colonne filtrabili con dropdown (valori distinti dalla tabella)
const FILTRO_COLONNE = [
  { label: 'Tipo Intervento', key: 'tipointervento' },
  { label: 'Linea',           key: 'linea'           },
  { label: 'Colata',          key: 'colata'          },
  { label: 'Posizione',       key: 'posizione'       },
  { label: 'Stato',           key: 'stato'           },
  { label: 'N° Macchina',     key: 'n_macchina'      },
  { label: 'Stato RID',       key: 'stato_rid'       },
  { label: 'Causa Freno',     key: 'causa_freno'     },
];

// ─── Helpers visivi ───────────────────────────────────────────────────────────
const getTipoConfig = (tipo) => {
  const t = tipo?.toUpperCase() || '';
  if (t === 'MONTATO')  return { color: 'text-emerald-600 dark:text-emerald-400 font-black', dot: 'bg-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/30' };
  if (t === 'SMONTATO') return { color: 'text-red-600 dark:text-red-400 font-black',         dot: 'bg-red-500',     bg: 'bg-red-500/10 border-red-500/30'         };
  return                       { color: 'text-slate-400 font-bold',                           dot: 'bg-slate-400',   bg: 'bg-slate-400/10 border-slate-400/30'     };
};

const getStatoConfig = (stato) => {
  const s = stato?.toUpperCase() || '';
  if (s === 'PRONTO')            return { color: 'text-emerald-600 dark:text-emerald-400 font-black', dot: 'bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,.5)]', bg: 'bg-emerald-500/10 border-emerald-500/30', label: 'Pronto'         };
  if (s.includes('RIPARAZIONE')) return { color: 'text-orange-500 font-black',                        dot: 'bg-orange-500',                                                       bg: 'bg-orange-500/10 border-orange-500/30',   label: 'In Riparazione' };
  return                               { color: 'text-slate-400 font-bold',                            dot: 'bg-slate-400',                                                        bg: 'bg-slate-400/10 border-slate-400/30',     label: stato || 'N/D'   };
};

// Config colori per Causa Freno (boolean): true → SI (rosso), false → NO (verde)
const getCausaFrenoConfig = (val) => {
  if (val) return { color: 'text-red-600 dark:text-red-400 font-black',         dot: 'bg-red-500',     bg: 'bg-red-500/10 border-red-500/30',     label: 'SI' };
  return         { color: 'text-emerald-600 dark:text-emerald-400 font-black', dot: 'bg-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/30', label: 'NO' };
};

// ─── Dropdown Export ──────────────────────────────────────────────────────────
const ExportDropdown = ({ data }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const voci = [
    { label: 'Esporta CSV',   icon: 'FileText', action: () => exportCSV(data)   },
    { label: 'Esporta Excel', icon: 'Sheet',    action: () => exportExcel(data) },
    { label: 'Esporta PDF',   icon: 'FileDown', action: () => exportPDF(data)   },
  ];
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-muted/40 hover:bg-muted/80 border border-border/40 hover:border-primary/30 rounded-2xl text-[11px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all duration-200 shadow-sm">
        <Icon name="Download" size={14} />
        <span className="hidden sm:inline">Esporta</span>
        <Icon name="ChevronDown" size={12} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-card border-2 border-border/60 rounded-2xl shadow-2xl overflow-hidden z-20 animate-in fade-in zoom-in-95 duration-150">
          {voci.map(v => (
            <button key={v.label} onClick={() => { v.action(); setOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-[12px] font-bold text-foreground/70 hover:text-foreground hover:bg-primary/5 transition-all duration-150 border-b border-border/20 last:border-0">
              <Icon name={v.icon} size={14} className="text-primary/60" />
              {v.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Pannello Impostazioni + Filtri ──────────────────────────────────────────
const ImpostazioniModal = ({
  colVisibili, setColVisibili, densita, setDensita,
  filtri, setFiltri, valoriDistinti, onClose,
}) => {
  const [tab, setTab] = useState('colonne'); // 'colonne' | 'filtri' | 'densita'

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const addFiltro = () => {
    if (filtri.length >= 4) return;
    setFiltri(prev => [...prev, { col: '', val: '', dateFrom: '', dateTo: '' }]);
  };

  const removeFiltro = (i) => setFiltri(prev => prev.filter((_, idx) => idx !== i));

  const updateFiltro = (i, field, value) => {
    setFiltri(prev => prev.map((f, idx) => idx === i ? { ...f, [field]: value, ...(field === 'col' ? { val: '', dateFrom: '', dateTo: '' } : {}) } : f));
  };

  const isDateCol = (col) => col === 'data_intervento' || col === 'data_rientro';
  const isBoolCol = (col) => col === 'causa_freno';

  const activeFiltriCount = filtri.filter(f => f.col && (f.val || f.dateFrom || f.dateTo)).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="relative w-full max-w-sm bg-card border-2 border-border/60 rounded-3xl shadow-2xl animate-in zoom-in-95 fade-in duration-300 overflow-hidden flex flex-col max-h-[85vh]">
        <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/60 to-transparent shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border/40 shrink-0">
          <div>
            <p className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/50 mb-0.5">Pannello</p>
            <h3 className="text-lg font-black uppercase tracking-tight">Impostazioni</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-muted/60 hover:bg-red-500/10 hover:text-red-500 border border-border/40 flex items-center justify-center transition-all text-muted-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex shrink-0 border-b border-border/40 px-2 pt-2">
          {[
            { id: 'colonne', label: 'Colonne', icon: 'Columns' },
            { id: 'filtri',  label: `Filtri${activeFiltriCount > 0 ? ` (${activeFiltriCount})` : ''}`, icon: 'Filter' },
            { id: 'densita', label: 'Densità', icon: 'AlignJustify' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 text-[10px] font-black uppercase tracking-widest rounded-t-xl transition-all duration-200 border-b-2
                ${tab === t.id
                  ? 'text-primary border-primary bg-primary/5'
                  : 'text-muted-foreground/50 border-transparent hover:text-muted-foreground'
                }`}
            >
              <Icon name={t.icon} size={11} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Contenuto tab */}
        <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: 'thin' }}>

          {/* TAB: Colonne */}
          {tab === 'colonne' && (
            <div className="flex flex-col gap-2">
              {COLONNE.filter(c => c.key !== '__azioni').map(col => (
                <label key={col.key} className="flex items-center justify-between cursor-pointer group">
                  <span className="text-sm font-bold text-foreground/70 group-hover:text-foreground transition-colors">{col.label}</span>
                  <div
                    onClick={() => setColVisibili(prev => ({ ...prev, [col.key]: !prev[col.key] }))}
                    className={`relative w-10 h-5 rounded-full transition-all duration-300 ${colVisibili[col.key] !== false ? 'bg-primary' : 'bg-muted/80 border border-border/60'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-300 ${colVisibili[col.key] !== false ? 'left-5' : 'left-0.5'}`} />
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* TAB: Filtri */}
          {tab === 'filtri' && (
            <div className="flex flex-col gap-4">
              <p className="text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/50">
                Aggiungi fino a 4 filtri. Per le date seleziona un intervallo.
              </p>

              {filtri.map((f, i) => (
                <div key={i} className="flex flex-col gap-2.5 p-4 rounded-2xl bg-muted/20 border border-border/30">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-black uppercase tracking-widest text-primary/60">Filtro {i + 1}</span>
                    <button onClick={() => removeFiltro(i)} className="w-6 h-6 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-all">
                      <Icon name="X" size={11} />
                    </button>
                  </div>

                  {/* Selezione colonna */}
                  <select
                    value={f.col}
                    onChange={(e) => updateFiltro(i, 'col', e.target.value)}
                    className="w-full bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all"
                  >
                    <option value="">— Seleziona colonna —</option>
                    {FILTRO_COLONNE.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    <option value="data_intervento">Data Intervento</option>
                    <option value="data_rientro">Data Rientro</option>
                  </select>

                  {/* Valore filtro (dropdown testo) */}
                  {f.col && !isDateCol(f.col) && !isBoolCol(f.col) && (
                    <select
                      value={f.val}
                      onChange={(e) => updateFiltro(i, 'val', e.target.value)}
                      className="w-full bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all"
                    >
                      <option value="">— Tutti —</option>
                      {(valoriDistinti[f.col] || []).map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  )}

                  {/* Valore filtro (SI / NO per Causa Freno) */}
                  {f.col && isBoolCol(f.col) && (
                    <select
                      value={f.val}
                      onChange={(e) => updateFiltro(i, 'val', e.target.value)}
                      className="w-full bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all"
                    >
                      <option value="">— Tutti —</option>
                      <option value="SI">SI</option>
                      <option value="NO">NO</option>
                    </select>
                  )}

                  {/* Date range */}
                  {f.col && isDateCol(f.col) && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black uppercase text-muted-foreground/50 w-6 shrink-0">Da</span>
                        <input type="date" value={f.dateFrom} onChange={(e) => updateFiltro(i, 'dateFrom', e.target.value)}
                          className="flex-1 bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black uppercase text-muted-foreground/50 w-6 shrink-0">A</span>
                        <input type="date" value={f.dateTo} onChange={(e) => updateFiltro(i, 'dateTo', e.target.value)}
                          className="flex-1 bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all" />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Bottone aggiungi filtro */}
              {filtri.length < 4 && (
                <button onClick={addFiltro} className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border-2 border-dashed border-border/40 hover:border-primary/40 hover:bg-primary/5 text-muted-foreground/40 hover:text-primary transition-all duration-200 text-[11px] font-black uppercase tracking-widest">
                  <Icon name="Plus" size={14} />
                  Aggiungi filtro
                </button>
              )}

              {/* Reset filtri */}
              {filtri.length > 0 && (
                <button onClick={() => setFiltri([])} className="flex items-center justify-center gap-2 w-full py-2.5 rounded-2xl bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 text-red-500/70 hover:text-red-500 transition-all text-[11px] font-black uppercase tracking-widest">
                  <Icon name="Trash2" size={12} />
                  Azzera tutti i filtri
                </button>
              )}
            </div>
          )}

          {/* TAB: Densità */}
          {tab === 'densita' && (
            <div className="flex flex-col gap-3">
              <p className="text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/50 mb-1">Spaziatura righe</p>
              {Object.keys(DENSITA).map(d => (
                <button
                  key={d}
                  onClick={() => setDensita(d)}
                  className={`flex items-center justify-between px-4 py-3 rounded-2xl border text-sm font-black uppercase tracking-wider transition-all duration-200
                    ${densita === d ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20' : 'bg-muted/30 border-border/40 text-muted-foreground hover:border-primary/30'}`}
                >
                  <span>{d.charAt(0).toUpperCase() + d.slice(1)}</span>
                  {densita === d && <Icon name="Check" size={14} />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-muted/40 border-t border-border/40 flex justify-end shrink-0">
          <button onClick={onClose} className="px-5 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary rounded-xl text-[11px] font-black uppercase tracking-widest transition-all">
            Fatto
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Badge attivi filtri ──────────────────────────────────────────────────────
const FiltriBadge = ({ filtri, onRemove }) => {
  const attivi = filtri.filter(f => f.col && (f.val || f.dateFrom || f.dateTo));
  if (attivi.length === 0) return null;
  const getLabelCol = (key) => {
    const found = [...FILTRO_COLONNE, { key: 'data_intervento', label: 'Data Intervento' }, { key: 'data_rientro', label: 'Data Rientro' }].find(c => c.key === key);
    return found?.label || key;
  };
  return (
    <div className="flex flex-wrap gap-2 px-1 shrink-0">
      {attivi.map((f, i) => {
        const label = getLabelCol(f.col);
        const val = f.val || `${f.dateFrom || '…'} → ${f.dateTo || '…'}`;
        return (
          <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <Icon name="Filter" size={10} />
            <span className="text-[10px] font-black uppercase tracking-widest">{label}: {val}</span>
            <button onClick={() => onRemove(i)} className="ml-1 w-4 h-4 rounded-full bg-primary/20 hover:bg-primary/40 flex items-center justify-center transition-all">
              <Icon name="X" size={9} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ─── Componente Principale ────────────────────────────────────────────────────
const RaddrizzatriciTable = () => {
  const [records, setRecords]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [searchTerm, setSearchTerm]         = useState('');
  const [sortConfig, setSortConfig]         = useState({ key: 'data_intervento', direction: 'desc' });
  const [displayLimit, setDisplayLimit]     = useState(40);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [storicoItem, setStoricoItem]       = useState(null);
  const [showSettings, setShowSettings]     = useState(false);
  const [densita, setDensita]               = useState('normale');
  const [colVisibili, setColVisibili]       = useState({});
  const [realtimeStatus, setRealtimeStatus] = useState('connecting');
  const [filtri, setFiltri]                 = useState([]); // [{ col, val, dateFrom, dateTo }]
  const [valoriDistinti, setValoriDistinti] = useState({});

  const scrollContainerRef = useRef(null);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from(TABLE_NAME).select('*');
      if (error) throw error;
      setRecords(data || []);
      // Calcola valori distinti per i filtri dropdown
      const dist = {};
      FILTRO_COLONNE.forEach(({ key }) => {
        if (key === 'causa_freno') return; // gestito a parte (SI/NO)
        dist[key] = [...new Set((data || []).map(r => r[key]).filter(Boolean))].sort();
      });
      setValoriDistinti(dist);
    } catch (err) {
      console.error('Errore fetch:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Realtime — PK è id (uuid) ────────────────────────────────────────────
  useEffect(() => {
    fetchRecords();
    const channel = supabase
      .channel('rd_cco_realtime_v1')
      .on('postgres_changes', { event: '*', schema: 'public', table: TABLE_NAME }, (payload) => {
        const { eventType, new: newRow, old: oldRow } = payload;

        setRecords(prev => {
          switch (eventType) {
            case 'INSERT':
              if (prev.some(r => r.id === newRow.id)) return prev;
              return [newRow, ...prev];
            case 'UPDATE':
              return prev.map(r => r.id === newRow.id ? { ...r, ...newRow } : r);
            case 'DELETE':
              return prev.filter(r => r.id !== oldRow.id);
            default:
              return prev;
          }
        });

        if (eventType === 'UPDATE') {
          setSelectedItem(prev => prev?.id === newRow.id ? { ...prev, ...newRow } : prev);
        }
        if (eventType === 'DELETE') {
          setSelectedItem(prev => prev?.id === oldRow.id ? null : prev);
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED')                            setRealtimeStatus('live');
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR') setRealtimeStatus('error');
      });

    return () => { supabase.removeChannel(channel); };
  }, [fetchRecords]);

  // ── Sort ─────────────────────────────────────────────────────────────────
  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  // ── Filtraggio e ordinamento ──────────────────────────────────────────────
  const allFiltered = records
    .filter(item => {
      // Search globale su tutte le colonne testuali + date
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const match = [
          item.n_macchina, item.tipointervento, item.linea, item.colata,
          item.posizione, item.stato, item.note, item.stato_rid,
          item.snrientrato, item.snsmontato, item.snmontato,
          item.causa_freno ? 'si' : 'no',
          fmtDatetime(item.data_intervento), fmtDatetime(item.data_rientro),
        ].some(v => v?.toString().toLowerCase().includes(s));
        if (!match) return false;
      }

      // Filtri aggiuntivi
      for (const f of filtri) {
        if (!f.col) continue;

        const isDate = f.col === 'data_intervento' || f.col === 'data_rientro';
        const isBool = f.col === 'causa_freno';

        if (isDate) {
          if (!f.dateFrom && !f.dateTo) continue;
          const val = item[f.col] ? new Date(item[f.col]) : null;
          if (!val) return false;
          if (f.dateFrom && val < new Date(f.dateFrom)) return false;
          if (f.dateTo) {
            const toDate = new Date(f.dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (val > toDate) return false;
          }
        } else if (isBool) {
          if (!f.val) continue;
          const itemVal = item.causa_freno ? 'SI' : 'NO';
          if (itemVal !== f.val) return false;
        } else {
          if (!f.val) continue;
          const itemVal = (item[f.col] ?? '').toString().toLowerCase();
          if (!itemVal.includes(f.val.toLowerCase())) return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      const av = a[sortConfig.key] ?? '', bv = b[sortConfig.key] ?? '';
      // Date sort
      if (sortConfig.key === 'data_intervento' || sortConfig.key === 'data_rientro') {
        const da = av ? new Date(av).getTime() : 0;
        const db = bv ? new Date(bv).getTime() : 0;
        return sortConfig.direction === 'asc' ? da - db : db - da;
      }
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  const visibleData = allFiltered.slice(0, displayLimit);

  // ── Scroll infinito ───────────────────────────────────────────────────────
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 120 && displayLimit < allFiltered.length) {
      setDisplayLimit(prev => prev + 30);
    }
  }, [displayLimit, allFiltered.length]);

  useEffect(() => {
    setDisplayLimit(40);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [searchTerm, filtri]);

  // ── Rimuovi singolo filtro dal badge ──────────────────────────────────────
  const removeFiltro = (i) => {
    setFiltri(prev => prev.filter((_, idx) => idx !== i));
  };

  const colAttive   = COLONNE.filter(c => c.key === '__azioni' || colVisibili[c.key] !== false);
  const tableMinWidth = colAttive.reduce((acc, c) => acc + c.width, 0);
  const padClass    = DENSITA[densita];

  const realtimeDot = {
    connecting: 'bg-amber-500',
    live:       'bg-emerald-500 animate-pulse shadow-[0_0_8px_#10b981]',
    error:      'bg-red-500',
  }[realtimeStatus];

  const realtimeLabel = {
    connecting: 'Connessione...',
    live:       'Live',
    error:      'Errore Realtime',
  }[realtimeStatus];

  const activeFiltriCount = filtri.filter(f => f.col && (f.val || f.dateFrom || f.dateTo)).length;

  return (
    <>
      <div className="w-full h-screen flex flex-col p-3 md:p-6 animate-in fade-in duration-700 overflow-hidden bg-background">

        {/* ── Header ── */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-4 md:mb-5 px-1 shrink-0">
          <div>
            <div className="flex items-baseline gap-3">
              <h2 className="text-4xl md:text-5xl font-black tracking-[-0.06em] uppercase leading-none text-foreground">
                RADDRIZZATRICI<span className="text-primary">CCO</span>
              </h2>
              {!loading && (
                <span className="text-[10px] font-black uppercase tracking-[.25em] text-muted-foreground/40 pb-1"></span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2 md:mt-3">
              <span className="h-[2px] w-6 bg-primary rounded-full" />
              <p className="text-[10px] font-black uppercase tracking-[.35em] text-muted-foreground/50">
                Sistema di Gestione Interventi Raddrizzatrici
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3 w-full md:w-auto flex-wrap">
            {/* Ricerca */}
            <div className="relative flex-1 min-w-0">
              <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary/60" />
              <input
                type="text"
                placeholder="Cerca macchina, tipo, linea..."
                className="w-full bg-muted/40 border border-border/40 rounded-2xl py-2.5 pl-9 pr-8 outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/10 transition-all text-sm font-semibold text-foreground placeholder:text-muted-foreground/40 shadow-sm"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-foreground transition-colors">
                  <Icon name="X" size={13} />
                </button>
              )}
            </div>

            <ExportDropdown data={allFiltered} />

            {/* Impostazioni + badge filtri attivi */}
            <button
              onClick={() => setShowSettings(true)}
              className={`relative flex items-center gap-2 px-3 md:px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm
                ${activeFiltriCount > 0
                  ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
                  : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
                }`}
            >
              <Icon name={activeFiltriCount > 0 ? 'Filter' : 'Settings2'} size={14} />
              <span className="hidden sm:inline">{activeFiltriCount > 0 ? `Filtri (${activeFiltriCount})` : 'Impostazioni'}</span>
              {activeFiltriCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-black flex items-center justify-center">
                  {activeFiltriCount}
                </span>
              )}
            </button>

            <button
              onClick={fetchRecords}
              disabled={loading}
              className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-2xl text-[11px] font-black uppercase tracking-widest text-primary transition-all duration-200 shadow-sm disabled:opacity-50"
            >
              <Icon name="RefreshCw" size={14} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Aggiorna</span>
            </button>
          </div>
        </div>

        {/* Badge filtri attivi */}
        {activeFiltriCount > 0 && (
          <div className="mb-3 shrink-0">
            <FiltriBadge filtri={filtri} onRemove={removeFiltro} />
          </div>
        )}

        {/* ── Tabella ── */}
        <div className="flex-1 bg-card border border-border/40 rounded-3xl shadow-xl overflow-hidden flex flex-col min-h-0">

          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-auto min-h-0"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.2) transparent', WebkitOverflowScrolling: 'touch' }}
          >
            <table className="border-collapse" style={{ minWidth: tableMinWidth, width: '100%' }}>

              {/* THEAD sticky */}
              <thead className="sticky top-0 z-10">
                <tr className="bg-muted/90 backdrop-blur-sm border-b border-border/50">
                  {colAttive.map((col, i) => (
                    <th
                      key={col.key}
                      onClick={() => !col.noSort && handleSort(col.key)}
                      style={{ width: col.width, minWidth: col.width }}
                      className={`
                        px-4 py-3.5 text-[10px] font-black uppercase tracking-[.18em]
                        transition-colors select-none whitespace-nowrap
                        ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'}
                        ${!col.noSort ? 'cursor-pointer hover:text-primary' : 'cursor-default'}
                        ${sortConfig.key === col.key ? 'text-primary' : 'text-muted-foreground/60'}
                        ${i === 0 ? 'pl-5' : ''}
                        ${i === colAttive.length - 1 ? 'pr-5' : ''}
                      `}
                    >
                      <div className={`inline-flex items-center gap-1.5 ${col.align === 'center' ? 'justify-center' : col.align === 'right' ? 'justify-end' : ''}`}>
                        {col.label}
                        {!col.noSort && sortConfig.key === col.key && (
                          <Icon name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={10} className="text-primary" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* TBODY */}
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={colAttive.length} className="py-40 text-center">
                      <div className="inline-flex flex-col items-center gap-4">
                        <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                        <span className="text-[10px] font-black uppercase tracking-[.3em] text-muted-foreground/40">Caricamento...</span>
                      </div>
                    </td>
                  </tr>
                ) : visibleData.length === 0 ? (
                  <tr>
                    <td colSpan={colAttive.length} className="py-32 text-center">
                      <div className="inline-flex flex-col items-center gap-3">
                        <Icon name="PackageSearch" size={36} className="text-muted-foreground/20" />
                        <span className="text-sm font-bold text-muted-foreground/40">Nessun intervento trovato</span>
                        <button onClick={() => { setSearchTerm(''); setFiltri([]); }} className="text-[10px] font-black uppercase tracking-widest text-primary/60 hover:text-primary transition-colors">
                          Azzera ricerca e filtri
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : visibleData.map((item, idx) => {
                  const tipoCfg  = getTipoConfig(item.tipointervento);
                  const statoCfg = getStatoConfig(item.stato);
                  const causaCfg = getCausaFrenoConfig(item.causa_freno);
                  const isEven   = idx % 2 === 0;

                  return (
                    <tr
                      key={item.id ?? idx}
                      className={`group border-b border-border/[0.15] transition-all duration-200 cursor-default
                        ${isEven ? 'bg-card hover:bg-primary/[0.05]' : 'bg-muted/[0.25] hover:bg-primary/[0.07]'}
                        hover:shadow-[inset_3px_0_0_0_hsl(var(--primary))]`}
                    >
                      {/* Data Intervento */}
                      {colVisibili['data_intervento'] !== false && (
                        <td className={`px-5 ${padClass} whitespace-nowrap`} style={{ width: COLONNE[0].width, minWidth: COLONNE[0].width }}>
                          <span className="font-mono text-[11px] font-bold text-muted-foreground/50 group-hover:text-foreground/70 transition-colors duration-200">
                            {fmtDatetime(item.data_intervento)}
                          </span>
                        </td>
                      )}

                      {/* N° Macchina */}
                      {colVisibili['n_macchina'] !== false && (
                        <td className={`px-4 ${padClass}`} style={{ width: COLONNE[1].width, minWidth: COLONNE[1].width }}>
                          <span className="text-[13px] font-black uppercase tracking-tight text-foreground/80 group-hover:text-foreground transition-colors duration-200">
                            {item.n_macchina || '---'}
                          </span>
                        </td>
                      )}

                      {/* Tipo Intervento */}
                      {colVisibili['tipointervento'] !== false && (
                        <td className={`px-4 ${padClass} text-center`} style={{ width: COLONNE[2].width, minWidth: COLONNE[2].width }}>
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border ${tipoCfg.bg}`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tipoCfg.dot}`} />
                            <span className={`text-[10px] uppercase tracking-widest whitespace-nowrap ${tipoCfg.color}`}>
                              {item.tipointervento || '---'}
                            </span>
                          </div>
                        </td>
                      )}

                      {/* Linea */}
                      {colVisibili['linea'] !== false && (
                        <td className={`px-4 ${padClass} text-center`} style={{ width: COLONNE[3].width, minWidth: COLONNE[3].width }}>
                          <span className="text-[12px] font-bold text-muted-foreground/60 group-hover:text-foreground/70 transition-colors">
                            {item.linea || '---'}
                          </span>
                        </td>
                      )}

                      {/* Colata */}
                      {colVisibili['colata'] !== false && (
                        <td className={`px-4 ${padClass} text-center`} style={{ width: COLONNE[4].width, minWidth: COLONNE[4].width }}>
                          <span className="text-[12px] font-bold text-muted-foreground/60 group-hover:text-foreground/70 transition-colors">
                            {item.colata || '---'}
                          </span>
                        </td>
                      )}

                      {/* Posizione */}
                      {colVisibili['posizione'] !== false && (
                        <td className={`px-4 ${padClass} text-center`} style={{ width: COLONNE[5].width, minWidth: COLONNE[5].width }}>
                          <span className="text-[12px] font-bold text-muted-foreground/60 group-hover:text-foreground/70 transition-colors">
                            {item.posizione || '---'}
                          </span>
                        </td>
                      )}

                      {/* Causa Freno */}
                      {colVisibili['causa_freno'] !== false && (
                        <td className={`px-4 ${padClass} text-center`} style={{ width: COLONNE[6].width, minWidth: COLONNE[6].width }}>
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border ${causaCfg.bg}`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${causaCfg.dot}`} />
                            <span className={`text-[10px] uppercase tracking-widest whitespace-nowrap ${causaCfg.color}`}>
                              {causaCfg.label}
                            </span>
                          </div>
                        </td>
                      )}

                      {/* Stato */}
                      {colVisibili['stato'] !== false && (
                        <td className={`px-4 ${padClass} text-center`} style={{ width: COLONNE[7].width, minWidth: COLONNE[7].width }}>
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border ${statoCfg.bg}`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statoCfg.dot}`} />
                            <span className={`text-[9px] font-black uppercase tracking-widest whitespace-nowrap ${statoCfg.color}`}>
                              {statoCfg.label}
                            </span>
                          </div>
                        </td>
                      )}

                      {/* Data Rientro */}
                      {colVisibili['data_rientro'] !== false && (
                        <td className={`px-4 ${padClass} text-center`} style={{ width: COLONNE[8].width, minWidth: COLONNE[8].width }}>
                          <span className="font-mono text-[11px] font-bold text-muted-foreground/40 group-hover:text-foreground/60 transition-colors whitespace-nowrap">
                            {fmtDatetime(item.data_rientro)}
                          </span>
                        </td>
                      )}

                      {/* Azioni */}
                      <td className={`px-3 ${padClass}`} style={{ width: COLONNE[9].width, minWidth: COLONNE[9].width }}>
                        <div className="flex items-center justify-center gap-2">
                          {/* Apri dettaglio */}
                          <button
                            onClick={() => setSelectedItem(item)}
                            className="inline-flex items-center justify-center gap-1.5 w-9 h-9 md:w-auto md:px-4 md:py-2 rounded-2xl bg-primary/5 border border-primary/20 text-primary/60 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:shadow-[0_0_15px_rgba(var(--primary-rgb),0.3)] hover:scale-105 active:scale-95 transition-all duration-200 text-[10px] font-black uppercase tracking-wider"
                          >
                            <Icon name="Eye" size={14} strokeWidth={2.5} />
                            <span className="hidden md:inline">Apri</span>
                          </button>

                          {/* Storico */}
                          <button
                            onClick={() => setStoricoItem(item)}
                            className="inline-flex items-center justify-center w-9 h-9 md:w-auto md:px-3 md:py-2 rounded-2xl bg-muted/30 border border-border/40 text-muted-foreground/40 hover:bg-blue-500/10 hover:text-blue-500 hover:border-blue-500/30 transition-all duration-200"
                          >
                            <Icon name="History" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Indicatore paginazione */}
            {displayLimit < allFiltered.length && (
              <div className="py-4 text-center" style={{ minWidth: tableMinWidth }}>
                <span className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/30">
                  Scorri per caricare altri — {allFiltered.length - displayLimit} rimanenti
                </span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="shrink-0 px-4 md:px-6 py-3 bg-muted/50 border-t border-border/50 flex justify-between items-center gap-3">
            <div className="flex items-center gap-3 md:gap-5 min-w-0">
              <div className="flex items-center gap-2 shrink-0">
                <div className={`w-1.5 h-1.5 rounded-full ${realtimeDot}`} />
                <span className="text-[9px] font-black uppercase tracking-[.25em] text-muted-foreground/50 hidden sm:inline">
                  {realtimeLabel}
                </span>
              </div>
              <div className="h-3 w-px bg-border/50 hidden sm:block" />
              <span className="text-[9px] font-black uppercase tracking-[.15em] text-primary/40 hidden sm:inline">
                RADDRIZZATRICI CCO
              </span>
            </div>
            <div className="flex items-center gap-2 md:gap-4 shrink-0">
              {(searchTerm || activeFiltriCount > 0) && (
                <span className="text-[9px] font-black uppercase tracking-[.15em] text-muted-foreground/40 hidden sm:inline">
                  {allFiltered.length} / {records.length}
                </span>
              )}
              <span className="bg-primary/10 border border-primary/20 text-primary px-3 md:px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm whitespace-nowrap">
                {allFiltered.length} interventi
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Dettaglio */}
      {selectedItem && (
        <Detagli_Intervento
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={(updated) => {
            setRecords(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
            setSelectedItem(updated);
          }}
        />
      )}

      {/* Modal Storico */}
      {storicoItem && (
        <StoricoRaddrizzatrice
          nMacchina={storicoItem.n_macchina}
          nome={storicoItem.n_macchina}
          onClose={() => setStoricoItem(null)}
        />
      )}

      {/* Modal Impostazioni */}
      {showSettings && (
        <ImpostazioniModal
          colVisibili={colVisibili}
          setColVisibili={setColVisibili}
          densita={densita}
          setDensita={setDensita}
          filtri={filtri}
          setFiltri={setFiltri}
          valoriDistinti={valoriDistinti}
          onClose={() => setShowSettings(false)}
        />
      )}
    </>
  );
};

export default RaddrizzatriciTable;